import { FC, ReactNode, useState, useEffect } from 'react';
import { Box, Theme } from '@mui/material';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import Text from '@/components/text';

interface Props {
  children: ReactNode;
  color: 'success' | 'info';
  isHighlighted?: boolean;
  showAnimation?: boolean;
  bold?: boolean;
  inline?: boolean;
  className?: string;
}

interface StyleProps {
  color: 'success' | 'info';
  isHighlighted: boolean;
  shouldAnimate: boolean;
}

const useStyles = createUseStyles<string, StyleProps>((theme: Theme) => ({
  highlightText: {
    '&.inline': {
      display: 'inline-block',
      verticalAlign: 'baseline',
    },
  },
  text: {
    textTransform: 'none',
    position: 'relative',
    color: ({ isHighlighted }) =>
      isHighlighted ? theme.palette.common.black : theme.palette.common.white,
    padding: '2px 4px',
    transition: 'color 0.3s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: ({ isHighlighted }) => (isHighlighted ? '100%' : '0%'),
      height: '100%',
      backgroundColor: ({ color }) =>
        color === 'success'
          ? theme.palette.surface.successHighlight.main
          : theme.palette.surface.infoHighlight.main,
      transformOrigin: 'left',
      zIndex: -1,
      borderRadius: 2,
      // Only apply transition when shouldAnimate is true (forward animation only)
      transition: ({ shouldAnimate }) => (shouldAnimate ? 'width 0.6s ease' : 'none'),
    },
  },
}));

const HighlightText: FC<Props> = ({
  children,
  color,
  isHighlighted = false,
  showAnimation = false,
  bold,
  inline,
  className,
}) => {
  // The internal state is used to simulate the highlighted animation, if not animating, the isHighlighted value is used
  const [internalHighlighted, setInternalHighlighted] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    if (showAnimation === true) {
      // First, disable transition and set highlight to false
      setShouldAnimate(false);
      setInternalHighlighted(false);

      // After delay, enable transition and set highlight to true
      timeoutId = setTimeout(() => {
        setShouldAnimate(true);
        setInternalHighlighted(true);
      }, 500);
    } else {
      // When not animating, use isHighlighted value
      setShouldAnimate(false);
      setInternalHighlighted(isHighlighted);
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showAnimation, isHighlighted]);

  // Determine the effective highlighted state
  const effectiveHighlighted =
    showAnimation !== undefined && showAnimation !== false ? internalHighlighted : isHighlighted;

  const styles = useStyles({
    color,
    isHighlighted: effectiveHighlighted,
    shouldAnimate,
  });

  return (
    <Box position="relative" zIndex={0} className={classNames(styles.highlightText, { inline })}>
      <Text className={classNames(styles.text, className)} bold={bold} inline={inline}>
        {children}
      </Text>
    </Box>
  );
};

export default HighlightText;
